import axios from "axios";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import CampaignSummaryCard from "./campaignSummaryCard";
import EntrepreneurReviewDashboard from "./EntrepreneurReviewDashboard";
import { normalizeInvestorPerks } from "../utils/normalizeInvestorPerks";
import { notifyUser } from "../utils/notifyUser";

function EntrepreneurDashboard({ user }) {
    const [formData, setFormData] = useState(null);
    const [, setFormDisabled] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const location = useLocation();
    const [activeTab, setActiveTab] = useState("summary");
    const [, setStatus] = useState(null);
    const transformUploadedFiles = (filesObj) => {
        const result = {};
        for(const [docType, value] of Object.entries(filesObj || {})) {
            if(Array.isArray(value)) {
                result[docType] = value;
            } else if(typeof value === "string") {
                result[docType] = [value];
            }
        }
        return result;
    };

    const fetchFormData = async () => {
        const email = user?.email;
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/entrepreneur-info`,
                {
                    params: { email },
                    withCredentials: true,
                },
            );

            if(typeof response.data !== "object") {
                throw new Error(
                    "Invalid response format: Expected JSON object",
                );
            }
            try {
                const data = response.data;

                // Process mediaUploads and campaignMediaUploads to ensure they're proper arrays
                const processedMediaUploads =
                    typeof data.mediaUploads === "string"
                        ? JSON.parse(data.mediaUploads)
                        : data.mediaUploads || [];

                const processedCampaignMediaUploads =
                    typeof data.campaignMediaUploads === "string"
                        ? JSON.parse(data.campaignMediaUploads)
                        : data.campaignMediaUploads || [];

                console.log("Media uploads from backend:", {
                    mediaUploads: processedMediaUploads,
                    campaignMediaUploads: processedCampaignMediaUploads,
                });

                setFormData({
                    ...data,
                    boardMembers:
                        typeof data.boardMembers === "string"
                            ? JSON.parse(data.boardMembers)
                            : data.boardMembers || [],
                    investorPerks: normalizeInvestorPerks(data.investorPerks),
                    uploadedFiles: transformUploadedFiles(data.uploadedFiles),
                    socialLinks:
                        typeof data.socialLinks === "string"
                            ? JSON.parse(data.socialLinks)
                            : data.socialLinks || [],
                    mediaUploads: processedMediaUploads,
                    campaignMediaUploads: processedCampaignMediaUploads,
                });
                setStatus(data.status);
                console.log(
                    "Raw uploadedFiles from backend:",
                    data.uploadedFiles,
                );

                if(data.status !== "approved") {
                    setFormDisabled(true);
                }
            } catch(parseError) {
                console.error("Error parsing form data", parseError);
                notifyUser(null, "error", "Invalid data format received from the server", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } catch(error) {
            console.error("Failed to fetch form data", error);
            notifyUser(null, "error", "Failed to load entrepreneur data", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    useEffect(() => {
        fetchFormData();
    }, []);

    // Set tab from URL query param
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get("tab");
        if(tab === "campaign") {
            setActiveTab("campaign");
        } else {
            setActiveTab("summary");
        }
    }, [location.search]);

    const handleEditToggle = () => {
        setIsEditing(!isEditing);
    };
    const handleInputChange = (e, index, type) => {
        const { name, value } = e.target;

        if(type === "socialLinks") {
            setFormData((prev) => {
                const updatedLinks = [...prev.socialLinks];
                updatedLinks[index] = value;
                return { ...prev, socialLinks: updatedLinks };
            });
            return;
        }

        if(type === "investorPerks") {
            const normalizedValue =
                name === "amount" ? value.replace(/\D/g, "") : value;
            setFormData((prev) => ({
                ...prev,
                investorPerks: normalizeInvestorPerks(prev.investorPerks).map(
                    (perk, perkIndex) => {
                        if(perkIndex === index) {
                            return { ...perk, [name]: normalizedValue };
                        }

                        return perk;
                    }
                ),
            }));
            return;
        }

        setFormData((prev) => ({ ...prev, [name]: value }));
    };
    const handleFileDelete = async (filePath, field, docType) => {
        console.log("Delete Request:", { filePath, field, docType });
        const userId = formData.id || user?.id;

        try {
            // Call backend to delete
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/entrepreneur/delete-file`,
                {
                    userId,
                    filePath,
                    docType,
                    field,
                },
                {
                    withCredentials: true,
                },
            );

            // Update state locally
            setFormData((prev) => {
                const updated = { ...prev };
                updated[field] = {
                    ...updated[field],
                    [docType]: (Array.isArray(updated[field]?.[docType])
                        ? updated[field][docType]
                        : [updated[field][docType]]
                    ).filter((f) => f !== filePath),
                };
                return updated;
            });

            notifyUser(null, "success", "File deleted", "toast", null, null, null, null, null, false, false, "top-center");
        } catch(err) {
            console.error("Delete error:", err);
            notifyUser(null, "error", "Failed to delete file", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };
    const handleFileUpload = async (files, field) => {
        // Validate files are selected
        if(!files || files.length === 0) {
            notifyUser(null, "error", "Please select at least one file to upload.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        const userId = formData.id || user?.id;

        const formDataToSend = new FormData();
        for(let file of files) {
            formDataToSend.append(
                field === "uploadedFiles" ? "files" : "mediaFiles",
                file,
            );
        }

        // Additional validation: ensure FormData contains files
        const uploadedFiles = formDataToSend.getAll(
            field === "uploadedFiles" ? "files" : "mediaFiles",
        );
        if(uploadedFiles.length === 0) {
            notifyUser(null, "error", "No valid files found to upload. Please try again.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        console.log("EntrepreneurDashboard upload attempt:", {
            fileCount: files.length,
            fieldType: field,
            formDataFiles: uploadedFiles.length,
        });

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/upload?userId=${userId}`,
                formDataToSend,
                {
                    withCredentials: true,
                },
            );
            const uploaded = res.data.files
                .filter(
                    (f) =>
                        f.type ===
                        (field === "uploadedFiles" ? "document" : "media"),
                )
                .map((f) => f.path);

            // If uploading media files, ensure they're added to mediaUploads
            if(field === "mediaUploads") {
                console.log("Adding to mediaUploads:", uploaded);
                // Store newly uploaded files in the mediaUploads array
                setFormData((prev) => ({
                    ...prev,
                    mediaUploads: [...(prev.mediaUploads || []), ...uploaded],
                }));
            } else {
                setFormData((prev) => ({
                    ...prev,
                    [field]: [...(prev[field] || []), ...uploaded],
                }));
            }

            console.log("EntrepreneurDashboard upload successful:", {
                uploadedCount: uploaded.length,
                fieldType: field,
            });
        } catch(err) {
            console.error("Upload error", err);
            if(
                err.response &&
                err.response.data &&
                err.response.data.message
            ) {
                notifyUser(null, "error", `Upload failed: ${err.response.data.message}`, "toast", null, null, null, null, null, false, false, "top-center");
            } else if(err.response && err.response.status === 400) {
                notifyUser(null, "error", "Invalid file upload. Please check your files and try again.", "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                notifyUser(null, "error", "Upload failed. Please try again.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        }
    };
    const handleSubmit = async () => {
        try {
            // IMPORTANT: Combine both mediaUploads and campaignMediaUploads to preserve existing media
            // This ensures we don't overwrite existing media with an empty array
            const combinedMediaUploads = [
                ...(formData.mediaUploads || []),
                ...(formData.campaignMediaUploads || []),
            ];

            const updatedFormData = {
                ...formData,
                dateFounded: formData.dateFounded?.split("T")[0],
                // Send the combined media uploads to ensure we don't lose existing files
                mediaUploads: combinedMediaUploads,
            };

            // console.log("Submitting data with combined mediaUploads:", combinedMediaUploads);

            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/update-entrepreneur-fields`,
                {
                    email: user?.email,
                    updates: updatedFormData,
                    auditUser: user?.email || "system",
                },
                {
                    withCredentials: true,
                },
            );
            if(response.status === 200) {
                // Clear re_edit flag after update
                await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/clear-reedit`,
                    {
                        email: user?.email,
                    },
                    {
                        withCredentials: true,
                    },
                );

                notifyUser(null, "success", "Details updated successfully", "toast", null, null, null, null, null, false, false, "top-center");

                // Small delay to ensure backend processing completes
                setTimeout(() => {
                    // Explicitly fetch updated data to ensure we have the latest state
                    fetchFormData();
                    setIsEditing(false);
                }, 500);
            } else {
                notifyUser(null, "error", "Update failed", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } catch(err) {
            console.error(err);
            notifyUser(null, "error", "Error while updating", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    return (
        <div className="font-poppins">
            <div className="min-h-screen bg-white px-4 py-6">
                <h1 className="text-2xl font-semibold fon-poppins text-center text-navy mb-6">
                    Entrepreneur Account Summary
                </h1>

                {activeTab === "summary" &&
                    (formData ? (
                        <>
                            {formData.re_edit === 1 && (
                                <div className="flex justify-end mb-4">
                                    <button
                                        onClick={handleEditToggle}
                                        className="aqi-primary-action rounded-xl bg-navy px-12 py-2 font-bold text-white shadow-md hover:bg-navy-2 font-poppins"
                                    >
                                        {isEditing
                                            ? "CANCEL EDIT"
                                            : "EDIT INFO"}
                                    </button>
                                </div>
                            )}
                            <EntrepreneurReviewDashboard
                                formData={formData}
                                formDisabled={!isEditing}
                                isEditing={isEditing}
                                onEditToggle={handleEditToggle}
                                onChange={handleInputChange}
                                handleSubmit={handleSubmit}
                                onFileUpload={handleFileUpload}
                                onFileDelete={handleFileDelete}
                                setActiveSection={() => {}}
                            />
                        </>
                    ) : (
                        <p className="text-center text-gray-600">Loading...</p>
                    ))}
                {activeTab === "campaign" && (
                    <CampaignSummaryCard user={user} />
                )}
            </div>
        </div>
    );
}

EntrepreneurDashboard.propTypes = {
    user: PropTypes.shape({
        email: PropTypes.string,
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
};
export default EntrepreneurDashboard;
